/**
 * Metadata for instrument parameters.
 */
export interface ParameterMetadata {
  key: string;
  label: string;
  min: number;
  max: number;
  defaultValue: number;
  step?: number;
  unit?: string; // "ms", "Hz", "%", etc.
  category?: string;
}

/**
 * Concrete parameter class that uses getter/setter functions.
 * This is the only parameter type we need - no subclasses!
 */
export class InstrumentParameter {
  readonly key: string;
  readonly label: string;
  readonly min: number;
  readonly max: number;
  readonly defaultValue: number;
  readonly step: number;
  readonly unit?: string;
  readonly category?: string;

  constructor(
    private getter: () => number,
    private setter: (value: number) => void,
    metadata: Partial<ParameterMetadata> & { key: string; label: string },
  ) {
    this.key = metadata.key;
    this.label = metadata.label;
    this.min = metadata.min ?? 0;
    this.max = metadata.max ?? 1;
    this.defaultValue = metadata.defaultValue ?? 0.5;
    this.step = metadata.step ?? 0.01;
    this.unit = metadata.unit;
    this.category = metadata.category;
  }

  /**
   * Get the current value of the parameter.
   */
  getValue(): number {
    return this.getter();
  }

  /**
   * Set the value of the parameter, clamped to min/max range.
   */
  setValue(value: number): void {
    const clamped = Math.max(this.min, Math.min(this.max, value));
    this.setter(clamped);
  }
}
